/**
 * User Communication Channel Types
 *
 * Defines contact methods associated with users.
 *
 * Per-channel types and the union type are derived from the schemas
 * defined below (single source of truth). Input types and factory methods are
 * hand-written because they serve a different purpose (creation from raw data).
 *
 * Embedded as subdocument in User entity - not a separate collection.
 * Users can have multiple channels of the same type (e.g., 2 phones).
 */

import { z } from "zod";
import { InvalidWhatsAppIdError } from "../errors/invalid-whatsapp-id.error.js";
import {
  E164PhoneNumber,
  E164PhoneNumberSchema,
  type E164PhoneNumber as E164PhoneNumberType,
} from "./phone.schemas.js";
import {
  EmailAddress,
  EmailAddressSchema,
  type EmailAddress as EmailAddressType,
} from "./email.schemas.js";
import {
  WhatsAppBsuid,
  WhatsAppBsuidSchema,
  type WhatsAppBsuid as WhatsAppBsuidType,
} from "./whatsapp-bsuid.schemas.js";
import {
  WhatsAppId,
  WhatsAppIdSchema,
  type WhatsAppId as WhatsAppIdType,
} from "./whatsapp-id.schemas.js";
import {
  PersonalLinkedInUrl,
  PersonalLinkedInUrlSchema,
  type PersonalLinkedInUrl as PersonalLinkedInUrlType,
} from "./linkedin-url.schemas.js";

export const UserCommunicationChannelTypeSchema = z.enum({
  WHATSAPP: "WHATSAPP",
  SMS: "SMS",
  EMAIL: "EMAIL",
  LINKEDIN: "LINKEDIN",
});

export type UserCommunicationChannelType = z.infer<
  typeof UserCommunicationChannelTypeSchema
>;

export const UserCommunicationChannelType =
  UserCommunicationChannelTypeSchema.enum;

const verificationRefinement = <
  T extends { isVerified: boolean; verifiedAt: Date | null },
>(
  c: T,
): boolean => c.isVerified === (c.verifiedAt !== null);

const verificationMessage =
  "verifiedAt must be non-null iff isVerified is true";

const whatsappChannelBase = z.object({
  channel: z.literal(UserCommunicationChannelType.WHATSAPP),
  isVerified: z.boolean(),
  verifiedAt: z.coerce.date().nullable(),
  addedAt: z.coerce.date(),
  isPrimary: z.boolean(),
});

// One row per WhatsApp account, naming it by the phone-form wa_id, the BSUID
// Meta aliases it with, or both — the two are the same inbox reached by two
// names. "Neither" is absent from the union, so it cannot be built.
export const whatsappChannelSchema = z
  .union([
    whatsappChannelBase.extend({
      identifier: WhatsAppIdSchema,
      bsuid: z.null(),
    }),
    whatsappChannelBase.extend({
      identifier: z.null(),
      bsuid: WhatsAppBsuidSchema,
    }),
    whatsappChannelBase.extend({
      identifier: WhatsAppIdSchema,
      bsuid: WhatsAppBsuidSchema,
    }),
  ])
  .refine(verificationRefinement, { message: verificationMessage });

export const smsChannelSchema = z
  .object({
    channel: z.literal(UserCommunicationChannelType.SMS),
    identifier: E164PhoneNumberSchema,
    isVerified: z.boolean(),
    verifiedAt: z.coerce.date().nullable(),
    addedAt: z.coerce.date(),
    isPrimary: z.boolean(),
  })
  .refine(verificationRefinement, { message: verificationMessage });

export const emailChannelSchema = z
  .object({
    channel: z.literal(UserCommunicationChannelType.EMAIL),
    identifier: EmailAddressSchema,
    isVerified: z.boolean(),
    verifiedAt: z.coerce.date().nullable(),
    addedAt: z.coerce.date(),
    isPrimary: z.boolean(),
  })
  .refine(verificationRefinement, { message: verificationMessage });

export const linkedinChannelSchema = z
  .object({
    channel: z.literal(UserCommunicationChannelType.LINKEDIN),
    identifier: PersonalLinkedInUrlSchema,
    isVerified: z.boolean(),
    verifiedAt: z.coerce.date().nullable(),
    addedAt: z.coerce.date(),
    isPrimary: z.boolean(),
  })
  .refine(verificationRefinement, { message: verificationMessage });

// A plain union, not `discriminatedUnion("channel", …)`: zod requires a unique
// literal discriminant per option and the WhatsApp arms all carry WHATSAPP.
// Valid rows still parse on their first matching arm, and an invalid one still
// reports the issue from the arm it got furthest through.
export const UserCommunicationChannelSchema = z.union([
  whatsappChannelSchema,
  smsChannelSchema,
  emailChannelSchema,
  linkedinChannelSchema,
]);

export type WhatsAppUserCommunicationChannel = z.output<
  typeof whatsappChannelSchema
>;

export type SmsUserCommunicationChannel = z.output<typeof smsChannelSchema>;

export type EmailUserCommunicationChannel = z.output<typeof emailChannelSchema>;

export type LinkedInUserCommunicationChannel = z.output<
  typeof linkedinChannelSchema
>;

export type UserCommunicationChannel = z.output<
  typeof UserCommunicationChannelSchema
>;

interface CommunicationChannelInputBase {
  readonly isPrimary?: boolean;
}

interface WhatsAppChannelInputBase extends CommunicationChannelInputBase {
  readonly channel: typeof UserCommunicationChannelType.WHATSAPP;
}

export type WhatsAppIdentifierPair =
  | { readonly identifier: WhatsAppIdType; readonly bsuid: null }
  | { readonly identifier: null; readonly bsuid: WhatsAppBsuidType }
  | { readonly identifier: WhatsAppIdType; readonly bsuid: WhatsAppBsuidType };

export type WhatsAppCommunicationChannelInput = WhatsAppChannelInputBase &
  WhatsAppIdentifierPair;

export interface SmsCommunicationChannelInput
  extends CommunicationChannelInputBase {
  readonly channel: typeof UserCommunicationChannelType.SMS;
  readonly identifier: E164PhoneNumberType;
}

export interface EmailCommunicationChannelInput
  extends CommunicationChannelInputBase {
  readonly channel: typeof UserCommunicationChannelType.EMAIL;
  readonly identifier: EmailAddressType;
}

export interface LinkedInCommunicationChannelInput
  extends CommunicationChannelInputBase {
  readonly channel: typeof UserCommunicationChannelType.LINKEDIN;
  readonly identifier: PersonalLinkedInUrlType;
}

export type CommunicationChannelInput =
  | WhatsAppCommunicationChannelInput
  | SmsCommunicationChannelInput
  | EmailCommunicationChannelInput
  | LinkedInCommunicationChannelInput;

export interface UserCommunicationChannelRawInput {
  readonly channel: UserCommunicationChannelType;

  readonly identifier: string;

  /**
   * WhatsApp only: the BSUID column when the caller already knows the pair.
   * Without it a lone identifier is still classified by its own shape, so a
   * BSUID-only account can be built by passing the BSUID as the identifier.
   */
  readonly bsuid?: string | null;

  readonly isVerified: boolean;

  readonly verifiedAt: Date | null;

  readonly addedAt: Date;

  readonly isPrimary: boolean;
}

function buildChannel<
  T extends {
    channel: UserCommunicationChannelType;
    identifier: string | null;
  },
>(
  channelProps: T,
  input: UserCommunicationChannelRawInput,
): T & {
  addedAt: Date;
  isPrimary: boolean;
  isVerified: boolean;
  verifiedAt: Date | null;
} {
  const commonProps = {
    addedAt: input.addedAt,
    isPrimary: input.isPrimary,
  };

  // Use explicit conditionals to enforce verification invariant
  if (input.isVerified) {
    // Verified state: verifiedAt must be a Date (use current date as fallback for invalid data)
    return {
      ...channelProps,
      ...commonProps,
      isVerified: true as const,
      verifiedAt: input.verifiedAt ?? new Date(),
    };
  } else {
    // Unverified state: verifiedAt must be null
    return {
      ...channelProps,
      ...commonProps,
      isVerified: false as const,
      verifiedAt: null,
    };
  }
}

/** Narrows to `never` so a rejected BSUID reads as an expression, not a branch. */
function raiseInvalidWhatsAppId(value: string): never {
  throw new InvalidWhatsAppIdError(value);
}

export const UserCommunicationChannel = {
  /**
   * Create a UserCommunicationChannel from raw input data.
   * @param input - Raw channel data with string identifier
   * @returns Strongly-typed UserCommunicationChannel
   */
  unsafeCreate(
    input: UserCommunicationChannelRawInput,
  ): UserCommunicationChannel {
    switch (input.channel) {
      case UserCommunicationChannelType.WHATSAPP: {
        // A caller naming both keeps both columns. Otherwise the one name it
        // gave is classified by its own shape: a BSUID and a phone-form wa_id
        // are disjoint by construction.
        const knownBsuid =
          input.bsuid === undefined || input.bsuid === null
            ? undefined
            : (WhatsAppBsuid.tryCreate(input.bsuid) ??
              raiseInvalidWhatsAppId(input.bsuid));
        if (knownBsuid !== undefined) {
          return buildChannel(
            {
              channel: input.channel,
              identifier: WhatsAppId.create(input.identifier),
              bsuid: knownBsuid,
            },
            input,
          );
        }
        const bsuid = WhatsAppBsuid.tryCreate(input.identifier);
        return buildChannel(
          bsuid === undefined
            ? {
                channel: input.channel,
                identifier: WhatsAppId.create(input.identifier),
                bsuid: null,
              }
            : { channel: input.channel, identifier: null, bsuid },
          input,
        );
      }
      case UserCommunicationChannelType.SMS: {
        return buildChannel(
          {
            channel: input.channel,
            identifier: E164PhoneNumber.unsafeCreate(input.identifier),
          },
          input,
        );
      }
      case UserCommunicationChannelType.EMAIL: {
        return buildChannel(
          {
            channel: input.channel,
            identifier: EmailAddress.unsafeCreate(input.identifier),
          },
          input,
        );
      }
      case UserCommunicationChannelType.LINKEDIN: {
        return buildChannel(
          {
            channel: input.channel,
            identifier: PersonalLinkedInUrl.unsafeCreate(input.identifier),
          },
          input,
        );
      }
    }
  },
} as const;
